import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { AppRoot } from './App';
import reportWebVitals from './reportWebVitals';

// Red de seguridad global: si algo truena, mostramos una pantalla amable con
// botón de recargar en vez de dejar la app en blanco.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, reportado: false }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('RADAR ErrorBoundary:', error, info?.componentStack);
    // Reporte AUTOMATICO al equipo tecnico (best-effort: si la red murio, no insiste)
    try {
      fetch('/api/error-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mensaje: String(error?.message || error).slice(0, 400),
          stack: String(info?.componentStack || error?.stack || '').slice(0, 800),
          url: window.location.href,
          usuario: (() => { try { return localStorage.getItem('radar_usuario_nombre') || 'sesion activa'; } catch { return 'sesion activa'; } })(),
        }),
      }).then(() => this.setState({ reportado: true })).catch(() => {});
    } catch {}
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F1F4F9', fontFamily: 'Inter, sans-serif', padding: 20 }}>
        <div style={{ background: '#fff', border: '1px solid #DFE5EE', borderRadius: 16, padding: '36px 40px', maxWidth: 440, textAlign: 'center', boxShadow: '0 20px 60px rgba(15,23,42,.08)' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 24 }}>⚠️</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>Se produjo un error inesperado</div>
          <div style={{ fontSize: 13.5, color: '#56657F', lineHeight: 1.55, marginBottom: 18 }}>
            El equipo técnico ya fue notificado automáticamente y está trabajando para resolverlo lo antes posible.
            Recargue la página para continuar — su información guardada no se pierde.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => window.location.reload()}
              style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Recargar RADAR
            </button>
            <button
              onClick={() => {
                fetch('/api/error-report', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ mensaje: '[REPORTE MANUAL] ' + String(this.state.error?.message || this.state.error).slice(0, 380), url: window.location.href, usuario: 'reporte manual del usuario' }),
                }).catch(() => {});
                this.setState({ reportado: true });
              }}
              disabled={this.state.reportado}
              style={{ background: '#fff', color: '#374151', border: '1px solid #D6DDE8', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: this.state.reportado ? .55 : 1 }}>
              {this.state.reportado ? '✓ Error reportado' : 'Reportar error'}
            </button>
          </div>
          {/* Auto-reparacion: si el error viene de estado local corrupto (sesion
              vieja, localStorage podrido), esto lo limpia de raiz. Cierra la
              sesion, si — pero un login limpio es mejor que una app muerta. */}
          <button
            onClick={() => {
              try { localStorage.clear(); sessionStorage.clear(); } catch {}
              try {
                if (window.indexedDB && indexedDB.databases) {
                  indexedDB.databases().then(dbs => dbs.forEach(d => d.name && indexedDB.deleteDatabase(d.name))).catch(() => {});
                }
              } catch {}
              setTimeout(() => window.location.replace('/'), 400);
            }}
            style={{ background: 'none', color: '#B91C1C', border: 'none', marginTop: 14, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
            ¿Sigue fallando? Restablecer RADAR (borra datos locales y pide iniciar sesión de nuevo)
          </button>
          <div className="radar-num" style={{ fontSize: 10.5, color: '#8593A9', marginTop: 18, fontFamily: 'monospace', wordBreak: 'break-word' }}>
            Ref: {String(this.state.error?.message || this.state.error).slice(0, 110)}
          </div>
        </div>
      </div>
    );
  }
}

// Detector de version nueva: los usuarios dejan pestanas abiertas dias y siguen
// corriendo un bundle viejo (asi un bug ya corregido "sigue" roto para ellos).
// Cada 5 min y al volver a la pestana, comparamos el hash del bundle publicado
// con el que corre; si difieren, banner para actualizar con un toque.
function UpdateChecker() {
  const [hayNueva, setHayNueva] = React.useState(false);
  React.useEffect(() => {
    const actual = (document.querySelector('script[src*="/static/js/main."]')?.src || '').match(/main\.[a-f0-9]+\.js/)?.[0];
    if (!actual) return;
    let visto = false;
    const check = async () => {
      if (visto || document.hidden) return;
      try {
        const html = await (await fetch('/', { cache: 'no-store' })).text();
        const pub = html.match(/main\.[a-f0-9]+\.js/)?.[0];
        if (pub && pub !== actual) { visto = true; setHayNueva(true); }
      } catch {}
    };
    const id = setInterval(check, 5 * 60000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  if (!hayNueva) return null;
  return (
    <div style={{ position: 'fixed', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, background: '#0F172A', color: '#fff', borderRadius: 12, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 12px 32px rgba(15,23,42,.35)', fontFamily: 'Inter, sans-serif', fontSize: 13, maxWidth: '92vw' }}>
      <span>Hay una nueva versión de RADAR disponible.</span>
      <button onClick={() => window.location.reload()}
        style={{ background: '#22C55E', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Actualizar
      </button>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppRoot />
      <UpdateChecker />
    </ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
